import { validateAddress } from '@commenda/utils/validations';
import { SecureInformationRequestFieldType } from '@prisma/client';
import { useMemo } from 'react';
import {
  IStateSecureInformationRequestField,
  IStateSecureInformationRequestGroup,
} from '../User/types';

const useSecureInformationRequestProgress = ({
  groups,
}: {
  groups: IStateSecureInformationRequestGroup[];
}): number => {
  // Function to determine if a field is complete
  const isFieldComplete = (field: IStateSecureInformationRequestField) => {
    if (field.type === SecureInformationRequestFieldType.MULTI_SELECT)
      return field.selectResponse?.length > 0;
    if (
      field.type === SecureInformationRequestFieldType.FILE ||
      field.type === SecureInformationRequestFieldType.MULTI_FILE
    )
      return field.files?.length > 0;
    if (field.type === SecureInformationRequestFieldType.ADDRESS)
      return validateAddress(field.address).result;
    return !!field.value;
  };

  // Calculate overall progress percentage from fields (not groups)
  const progressPercentage = useMemo(() => {
    if (!groups || groups.length === 0) return 0;

    const { totalFields, completedFields } = groups.reduce(
      (acc, group) => {
        const groupTotal = group.fields.length;
        const groupCompleted = group.fields.reduce((count, field) => {
          return count + (isFieldComplete(field) ? 1 : 0);
        }, 0);
        return {
          totalFields: acc.totalFields + groupTotal,
          completedFields: acc.completedFields + groupCompleted,
        };
      },
      { totalFields: 0, completedFields: 0 }
    );

    if (totalFields === 0) return 0;
    return Math.round((completedFields / totalFields) * 100);
  }, [groups]);

  return progressPercentage;
};

export { useSecureInformationRequestProgress };
